/**
 * The one place that decides what a quotation's total is on screen.
 *
 * Every view of a quotation must print the same total:
 *   - the admin detail page    (/admin/quotations/create/:quotationId)
 *   - the admin download page  (/admin/quotations/download/:quotationId)
 *   - the customer link page   (/quotation/:quotationId)
 *   - the PDF document
 *
 * They used to disagree. The admin detail page added up the rows it shows, while
 * the other three printed the totals stored on the quotation (`totalAmount` /
 * `discountedTotalAmount`). A stored total can be out of date, so the pages
 * could show different numbers for the same quotation.
 *
 * The rule is the admin detail page's rule: the total is the sum of the rows the
 * reader can actually see. A total built this way can never disagree with the
 * rows printed above it.
 */

/**
 * The additional items an admin attached to one transfer, as a list of groups.
 *
 * Current transfers store them grouped (`additionalItemsGroups`). Older ones
 * store a single flat list (`additionalItemsQuotation`), which is returned here
 * as one group so callers only ever deal with one shape.
 *
 * @param {Object} booking one transfer of the quotation
 * @returns {Array<{heading: string, items: Array<Object>}>}
 */
export const getAdditionalGroups = (booking) => {
  if (booking?.additionalItemsGroups?.length > 0) {
    return booking.additionalItemsGroups;
  }

  if (booking?.additionalItemsQuotation?.length > 0) {
    return [
      {
        heading: booking.additionalItemsQuotationHeader || "",
        items: booking.additionalItemsQuotation,
      },
    ];
  }

  return [];
};

/**
 * Every additional item of one transfer, without the grouping.
 *
 * @param {Object} booking one transfer of the quotation
 * @returns {Array<Object>}
 */
export const getAllAdditionalItems = (booking) =>
  getAdditionalGroups(booking).flatMap((group) => group.items || []);

// Adds up `totalPrice` over a list of items. A missing price counts as 0.
const sumItemTotalPrices = (items) =>
  (items || []).reduce((sum, item) => sum + Number(item?.totalPrice || 0), 0);

/**
 * What one transfer costs the customer: its priced rows (vehicle, meet & assist,
 * child seats, ...) after discount, plus its additional items.
 *
 * Only a discounted row carries `discountedPrice`; every other row is charged at
 * its `totalPrice`.
 *
 * @param {Object} booking one transfer of the quotation
 * @returns {number} amount in yen
 */
export const computeBookingSubtotal = (booking) => {
  const bookingItemsTotal = (booking?.bookingItems || []).reduce(
    (sum, bookingItem) =>
      sum + Number(bookingItem?.discountedPrice ?? bookingItem?.totalPrice ?? 0),
    0
  );

  return bookingItemsTotal + sumItemTotalPrices(getAllAdditionalItems(booking));
};

/**
 * The total of the quotation-level ("standalone") items, which belong to the
 * quotation itself rather than to a transfer. They are never discounted.
 *
 * @param {Array<{items: Array<Object>}>} standaloneGroups
 * @returns {number} amount in yen
 */
export const computeStandaloneItemsTotal = (standaloneGroups) =>
  sumItemTotalPrices((standaloneGroups || []).flatMap((group) => group.items || []));

/**
 * The quotation total every page shows: all transfers plus the standalone items.
 *
 * `standaloneGroups` is a separate argument because the admin page keeps the
 * standalone items of a quotation that is not saved yet in local state. Pages
 * that only display a saved quotation can leave it out.
 *
 * @param {Object} quotationDetail the quotation as returned by the detail API
 * @param {Array<Object>} [standaloneGroups] defaults to the quotation's saved groups
 * @returns {number} amount in yen
 */
export const computeQuotationTotalAmount = (
  quotationDetail,
  standaloneGroups = quotationDetail?.standaloneItemsGroups
) => {
  const bookingsTotal = (quotationDetail?.bookings || []).reduce(
    (sum, booking) => sum + computeBookingSubtotal(booking),
    0
  );

  return bookingsTotal + computeStandaloneItemsTotal(standaloneGroups);
};
